import React, { useState, useEffect } from 'react';
import { usePerformance } from '../contexts/PerformanceContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

const PerformanceAnalytics = () => {
  const { 
    analytics, 
    reviewCycles, 
    fetchPerformanceAnalytics, 
    fetchReviewCycles 
  } = usePerformance();
  const { hasAnyRole } = useAuth();
  
  const [selectedCycle, setSelectedCycle] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      if (reviewCycles.length === 0) {
        await fetchReviewCycles();
      }
      await fetchPerformanceAnalytics(selectedCycle ? { cycle_id: selectedCycle } : {});
      setLoading(false);
    };

    loadData();
  }, [selectedCycle]);

  const handleCycleChange = (e) => {
    setSelectedCycle(e.target.value);
  };

  const getRatingDisplayName = (rating) => {
    const names = {
      exceptional: 'Exceptional',
      exceeds_expectations: 'Exceeds Expectations',
      meets_expectations: 'Meets Expectations',
      below_expectations: 'Below Expectations',
      unsatisfactory: 'Unsatisfactory'
    };
    return names[rating] || rating;
  };

  const getRatingColor = (rating) => {
    const colors = {
      exceptional: 'bg-green-500',
      exceeds_expectations: 'bg-blue-500',
      meets_expectations: 'bg-yellow-500',
      below_expectations: 'bg-orange-500',
      unsatisfactory: 'bg-red-500'
    };
    return colors[rating] || 'bg-gray-500';
  };

  if (!hasAnyRole(['hr', 'admin', 'manager'])) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
            <p className="text-gray-600 mb-6">
              You don't have permission to view performance analytics. This feature is available for HR, Admin, and Manager users only.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Performance Analytics</h1>
            <p className="text-gray-600 mt-2">Insights and metrics for performance reviews</p>
          </div>
          
          {/* Cycle Filter */}
          <div className="min-w-0 flex-1 max-w-xs">
            <label htmlFor="cycle" className="block text-sm font-medium text-gray-700 mb-1">
              Review Cycle
            </label>
            <select
              id="cycle"
              value={selectedCycle}
              onChange={handleCycleChange}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">All Cycles</option>
              {reviewCycles.map(cycle => (
                <option key={cycle.id} value={cycle.id}>{cycle.name}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner size="lg" text="Loading analytics..." />
        ) : (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                        <span className="text-white text-sm font-medium">📊</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Total Reviews</dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {analytics?.total_reviews || 0}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                        <span className="text-white text-sm font-medium">✓</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {analytics?.completed_reviews || 0}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                        <span className="text-white text-sm font-medium">%</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Completion Rate</dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {analytics?.completion_rate || 0}%
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                        <span className="text-white text-sm font-medium">★</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Avg Score</dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {analytics?.avg_overall_score || 0}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Rating Distribution */}
            {analytics?.rating_distribution && Object.keys(analytics.rating_distribution).length > 0 && (
              <div className="bg-white shadow rounded-lg mb-8">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Rating Distribution</h3>
                  <p className="text-gray-600 text-sm mt-1">Breakdown of performance ratings across completed reviews</p>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {Object.entries(analytics.rating_distribution).map(([rating, count]) => {
                      const total = analytics.completed_reviews || 1;
                      const percentage = (count / total) * 100;
                      
                      return (
                        <div key={rating} className="flex items-center">
                          <div className="w-32 text-sm text-gray-700">
                            {getRatingDisplayName(rating)}
                          </div>
                          <div className="flex-1 mx-4">
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div
                                className={`h-3 rounded-full ${getRatingColor(rating)}`}
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                          <div className="w-20 text-sm text-gray-700 text-right">
                            {count} ({Math.round(percentage)}%)
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Progress Overview */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Review Progress Overview</h3>
                <p className="text-gray-600 text-sm mt-1">Visual representation of review completion progress</p>
              </div>
              <div className="p-6">
                {analytics && analytics.total_reviews > 0 ? (
                  <div className="space-y-6">
                    {/* Overall Progress */}
                    <div>
                      <div className="flex justify-between text-sm font-medium text-gray-700 mb-2">
                        <span>Overall Completion</span>
                        <span>{analytics.completed_reviews} of {analytics.total_reviews} reviews</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-4">
                        <div
                          className="bg-green-600 h-4 rounded-full transition-all duration-300"
                          style={{ width: `${analytics.completion_rate}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0%</span>
                        <span>{analytics.completion_rate}% Complete</span>
                        <span>100%</span>
                      </div>
                    </div>

                    {/* Performance Score Distribution */}
                    {analytics.avg_overall_score > 0 && (
                      <div>
                        <div className="flex justify-between text-sm font-medium text-gray-700 mb-2">
                          <span>Average Performance Score</span>
                          <span>{analytics.avg_overall_score}/1.0</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-4">
                          <div
                            className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                            style={{ width: `${analytics.avg_overall_score * 100}%` }}
                          ></div>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>Poor (0.0)</span>
                          <span>{Math.round(analytics.avg_overall_score * 100)}%</span>
                          <span>Excellent (1.0)</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <h4 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h4>
                    <p className="text-gray-500">No performance review data found for the selected criteria.</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PerformanceAnalytics;